import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { lenderApi, handleApiError } from '@/services/api';
import { auth } from '@/lib/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { DiditVerification, type EntityType } from '@/components/verification/DiditVerification';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ArrowLeft, ArrowRight, Eye, EyeOff, CheckCircle2, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AddressAutocomplete } from '@/components/broker-registration/AddressAutocomplete';
import {
  LOAN_PRODUCT_TYPES, LENDER_TYPES, COLLATERAL_OPTIONS, FINANCING_AMOUNTS,
  CANADIAN_PROVINCES, US_STATES, EUROPEAN_COUNTRIES, OTHER_COUNTRIES,
  LENDER_REGISTRATION_TRANSLATIONS, TERMS_AND_CONDITIONS,
  INITIAL_FORM_DATA, LenderRegistrationFormData,
} from '@/constants/lenderRegistration';

const HOW_HEARD_OPTIONS = [
  { key: 'facebook',         en: 'Facebook',         fr: 'Facebook' },
  { key: 'linkedin',         en: 'LinkedIn',          fr: 'LinkedIn' },
  { key: 'instagram',        en: 'Instagram',         fr: 'Instagram' },
  { key: 'networking_group', en: 'Networking Group',  fr: 'Groupe de réseautage' },
  { key: 'youtube',          en: 'YouTube',           fr: 'YouTube' },
  { key: 'google_search',    en: 'Google Search',     fr: 'Recherche Google' },
  { key: 'friends',          en: 'Friends',           fr: 'Amis' },
  { key: 'family',           en: 'Family',            fr: 'Famille' },
  { key: 'other',            en: 'Other',             fr: 'Autre' },
];

type Language = 'en' | 'fr';

export default function RegisterLender() {
  const [step, setStep] = useState(1);
  const [lang, setLang] = useState<Language>('en');
  const [formData, setFormData] = useState<LenderRegistrationFormData>(INITIAL_FORM_DATA);
  const [entityType, setEntityType] = useState<EntityType>('individual');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const t = LENDER_REGISTRATION_TRANSLATIONS[lang];

  const updateField = (name: keyof LenderRegistrationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => { const n = {...prev}; delete n[name]; return n; });
    }
  };

  const toggleArrayField = (name: keyof LenderRegistrationFormData, value: string) => {
    const current = formData[name] as string[];
    const updated = current.includes(value) 
      ? current.filter(v => v !== value)
      : [...current, value];
    updateField(name, updated);
  };

  const validateStep = (currentStep: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (currentStep === 1) {
      if (!formData.firstName) newErrors.firstName = t.errors.required;
      if (!formData.lastName) newErrors.lastName = t.errors.required;
      if (!formData.email) newErrors.email = t.errors.required;
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t.errors.invalidEmail;
      if (!formData.password) newErrors.password = t.errors.required;
      else if (formData.password.length < 8) newErrors.password = t.errors.passwordLength;
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = t.errors.passwordMismatch;
      if (!formData.companyName) newErrors.companyName = t.errors.required;
      if (!formData.address) newErrors.address = t.errors.required;
      if (!formData.phoneNumber) newErrors.phoneNumber = t.errors.required;
      if (formData.howHeard.length === 0) newErrors.howHeard = t.errors.required;
    } else if (currentStep === 2) {
      if (formData.lenderTypes.length === 0) newErrors.lenderTypes = t.errors.required;
      if (formData.loanProductTypes.length === 0) newErrors.loanProductTypes = t.errors.required;
      if (formData.collateralOptions.length === 0) newErrors.collateralOptions = t.errors.required;
      if (!formData.minRange) newErrors.minRange = t.errors.required;
      if (!formData.maxRange) newErrors.maxRange = t.errors.required;
      if (!formData.debtServiceCoverageRatio) newErrors.debtServiceCoverageRatio = t.errors.required;
    } else if (currentStep === 3) {
      if (!formData.creditScoreRequired) newErrors.creditScoreRequired = t.errors.required;
      if (!formData.foreignCreditScoreAccepted) newErrors.foreignCreditScoreAccepted = t.errors.required;
      if (!formData.foreignCollateralAccepted) newErrors.foreignCollateralAccepted = t.errors.required;
    } else if (currentStep === 4) {
      if (!formData.agreeTerms) newErrors.agreeTerms = t.errors.required;
      if (!formData.backgroundCheckAuth) newErrors.backgroundCheckAuth = t.errors.required;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    setLoading(true);
    try {
      const result = await lenderApi.register(formData, lang);
      void result;

      await signInWithEmailAndPassword(auth, formData.email, formData.password);

      toast({ title: lang === 'en' ? 'Registration successful!' : 'Inscription réussie!' });
      setStep(5);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      const message = handleApiError(error);
      toast({ title: lang === 'en' ? 'Registration failed' : "Échec de l'inscription", description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationApproved = () => {
    setSubmitSuccess(true);
  };

  const handleVerificationDeclined = () => {};

  const stepNames = lang === 'en' 
    ? ['Profile', 'Lending Criteria', 'Jurisdictions', 'Terms', 'Verification'] 
    : ['Profil', 'Critères', 'Juridictions', 'Conditions', 'Vérification'];

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center py-8 px-4">
        <div className="max-w-xl mx-auto bg-white border border-slate-200 p-10 rounded-xl text-center shadow-xl">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#1D314D] mt-4">{t.success.title}</h2>
          <p className="text-slate-600 mt-2">
            {t.success.message} <strong>{formData.firstName} {formData.lastName}</strong> {t.success.from} <strong>{formData.companyName}</strong>!
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 text-left mt-6 mb-6 rounded">
            <h3 className="font-bold text-blue-900 mb-3">{t.success.nextSteps}</h3>
            <ol className="space-y-2 text-blue-800 list-decimal list-inside">
              <li>{t.success.step1}</li>
              <li>{t.success.step2} <strong>{formData.email}</strong></li>
              <li>{t.success.step3}</li>
            </ol>
          </div>
          <p className="text-slate-500 text-sm mb-4">
            {lang === 'en' 
              ? 'Your identity verification is complete and your account is now active.'
              : "Votre vérification d'identité est complétée et votre compte est maintenant actif."}
          </p>
          <Button onClick={() => navigate('/login')} className="bg-[#1D314D] hover:bg-[#2E4663]">
            {t.buttons.backToLogin}
          </Button>
        </div>
      </div>
    );
  }

  const MultiSelectGroup = ({ title, options, name, required }: { title: string; options: { key: string; en: string; fr: string }[]; name: keyof LenderRegistrationFormData; required?: boolean }) => (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-[#1D314D] mb-3">{title} {required && <span className="text-red-500">*</span>}</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {options.map(opt => (
          <div key={opt.key} onClick={() => toggleArrayField(name, opt.key)}
            className={cn("cursor-pointer p-3 text-center rounded-lg border transition-all text-sm",
              (formData[name] as string[]).includes(opt.key) ? "bg-[#1D314D] text-white border-[#2E4663]" : "bg-white text-slate-800 hover:bg-slate-50 border-slate-300"
            )}>
            {opt[lang]}
          </div>
        ))}
      </div>
      {errors[name] && <p className="text-red-500 text-xs mt-2">{errors[name]}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4">
      <div className="flex justify-center mb-6">
        <a href="/">
          <img src="/images/acquizgroup-logo.png" alt="AcquizGroup" className="h-12 object-contain" />
        </a>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] rounded-t-xl p-6 shadow-lg">
          <div className="flex items-center justify-between gap-4 mb-2">
            <div className="flex-shrink-0 w-32"></div>
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center">
              {t.title}
            </h1>
            <div className="flex-shrink-0 w-32 flex justify-end gap-2">
              <Button variant={lang === 'en' ? 'default' : 'outline'} size="sm" onClick={() => setLang('en')} 
                className={lang === 'en' ? "bg-white/20 text-white hover:bg-white/30 border-0" : "border-white/50 text-white hover:bg-white/10"}>
                EN
              </Button>
              <Button variant={lang === 'fr' ? 'default' : 'outline'} size="sm" onClick={() => setLang('fr')}
                className={lang === 'fr' ? "bg-[#CEAB65] text-[#1D314D] hover:bg-[#d4b875] border-0" : "border-white/50 text-white hover:bg-white/10"}>
                FR
              </Button>
            </div>
          </div>
          <p className="text-white/70 text-center mt-2">
            {lang === 'en' ? 'Step' : 'Étape'} {step} {lang === 'en' ? 'of' : 'de'} 5
          </p>

          <div className="flex justify-between gap-3 mt-6">
            {[1, 2, 3, 4, 5].map(s => {
              const isActive = s === step;
              const isCompleted = s < step;
              let bgClass = 'bg-gray-700';
              if (isActive) bgClass = 'bg-[#CEAB65]';
              if (isCompleted) bgClass = 'bg-emerald-500';

              return (
                <div
                  key={s}
                  onClick={() => s <= step && step < 5 && setStep(s)}
                  className={cn(
                    "flex-1 py-3 px-2 rounded-lg text-center font-bold cursor-pointer transition-all text-white hover:opacity-90",
                    bgClass
                  )}
                >
                  {s}. <span className={isActive || isCompleted ? 'inline' : 'hidden'}>{stepNames[s - 1]}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-b-xl p-6 md:p-8 shadow-lg">

        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#1D314D] mb-6">{t.step1.title}</h2>

            <div className="mb-2">
              <Label>
                {lang === 'en' ? 'Are you registering as an individual or a financial institution?' : "Vous inscrivez-vous en tant qu'individu ou institution financière?"}
                {' '}<span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div
                  onClick={() => setEntityType('individual')}
                  className={cn("cursor-pointer p-4 text-center rounded-lg border transition-all text-sm font-medium",
                    entityType === 'individual' ? "bg-[#1D314D] text-white border-[#2E4663]" : "bg-white text-slate-800 hover:bg-slate-50 border-slate-300"
                  )}
                >
                  {lang === 'en' ? 'Individual Lender' : 'Prêteur individuel'}
                </div>
                <div
                  onClick={() => setEntityType('business')}
                  className={cn("cursor-pointer p-4 text-center rounded-lg border transition-all text-sm font-medium",
                    entityType === 'business' ? "bg-[#1D314D] text-white border-[#2E4663]" : "bg-white text-slate-800 hover:bg-slate-50 border-slate-300"
                  )}
                >
                  {lang === 'en' ? 'Financial Institution' : 'Institution financière'}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t.step1.firstName} <span className="text-red-500">*</span></Label>
                <Input value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} className={errors.firstName ? 'border-red-500' : ''} />
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <Label>{t.step1.lastName} <span className="text-red-500">*</span></Label>
                <Input value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} className={errors.lastName ? 'border-red-500' : ''} />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>
            <div>
              <Label>{t.step1.email} <span className="text-red-500">*</span></Label>
              <Input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className={errors.email ? 'border-red-500' : ''} />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
            <div className="relative">
              <Label>{t.step1.password} <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={e => updateField('password', e.target.value)} placeholder={t.step1.passwordPlaceholder} className={errors.password ? 'border-red-500 pr-10' : 'pr-10'} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>
            <div className="relative">
              <Label>{t.step1.confirmPassword} <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={e => updateField('confirmPassword', e.target.value)} className={errors.confirmPassword ? 'border-red-500 pr-10' : 'pr-10'} />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              </div>
              {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>
            <div>
              <Label>{t.step1.companyName} <span className="text-red-500">*</span></Label>
              <Input value={formData.companyName} onChange={e => updateField('companyName', e.target.value)} className={errors.companyName ? 'border-red-500' : ''} />
            </div>
            <div>
              <Label>{t.step1.address} <span className="text-red-500">*</span></Label>
              <AddressAutocomplete
                value={formData.address}
                onChange={(value) => updateField('address', value)}
                onAddressSelect={(components) => {
                  updateField('address', `${components.streetAddress}, ${components.city}, ${components.stateProvince} ${components.zipPostalCode}`);
                }}
                placeholder={lang === 'en' ? 'Start typing your address...' : 'Commencez à taper votre adresse...'}
                error={!!errors.address}
              />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            </div>
            <div>
              <Label>{t.step1.phoneNumber} <span className="text-red-500">*</span></Label>
              <Input type="tel" value={formData.phoneNumber} onChange={e => updateField('phoneNumber', e.target.value)} className={errors.phoneNumber ? 'border-red-500' : ''} />
            </div>
            <div>
              <Label>
                {lang === 'en'
                  ? 'How did you hear about us?'
                  : 'Comment avez-vous entendu parler de nous?'}
                {' '}<span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {HOW_HEARD_OPTIONS.map(opt => (
                  <div
                    key={opt.key}
                    onClick={() => toggleArrayField('howHeard', opt.key)}
                    className={cn(
                      "cursor-pointer p-3 text-center rounded-lg border transition-all text-sm",
                      (formData.howHeard as string[]).includes(opt.key)
                        ? "bg-[#1D314D] text-white border-[#2E4663]"
                        : "bg-white text-slate-800 hover:bg-slate-50 border-slate-300"
                    )}
                  >
                    {opt[lang]}
                  </div>
                ))}
              </div>
              {errors.howHeard && (
                <p className="text-red-500 text-xs mt-2">{errors.howHeard}</p>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#1D314D]">{t.step2.title}</h2>
            <MultiSelectGroup title={t.step2.lenderTypes} options={LENDER_TYPES} name="lenderTypes" required />
            <MultiSelectGroup title={t.step2.loanProductTypes} options={LOAN_PRODUCT_TYPES} name="loanProductTypes" required />
            <MultiSelectGroup title={t.step2.collateralOptions} options={COLLATERAL_OPTIONS} name="collateralOptions" required />
            
            <h3 className="text-lg font-medium text-[#1D314D] pt-4 border-t">{t.step2.selectionCriteria}</h3>
            
            <div className="bg-slate-50 p-4 rounded-lg space-y-4">
              <h4 className="font-medium text-[#1D314D]">{t.step2.financingAmount}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t.step2.minAmount} <span className="text-red-500">*</span></Label>
                  <Select value={formData.minRange} onValueChange={v => updateField('minRange', v)}>
                    <SelectTrigger><SelectValue placeholder={t.select} /></SelectTrigger>
                    <SelectContent>{FINANCING_AMOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t.step2.maxAmount} <span className="text-red-500">*</span></Label>
                  <Select value={formData.maxRange} onValueChange={v => updateField('maxRange', v)}>
                    <SelectTrigger><SelectValue placeholder={t.select} /></SelectTrigger>
                    <SelectContent>{FINANCING_AMOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg space-y-4">
              <h4 className="font-medium text-[#1D314D]">{t.step2.grossRevenue}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t.step2.minGrossRevenue}</Label>
                  <Select value={formData.minGrossRevenue} onValueChange={v => updateField('minGrossRevenue', v)}>
                    <SelectTrigger><SelectValue placeholder={t.select} /></SelectTrigger>
                    <SelectContent>{FINANCING_AMOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t.step2.maxGrossRevenue}</Label>
                  <Select value={formData.maxGrossRevenue} onValueChange={v => updateField('maxGrossRevenue', v)}>
                    <SelectTrigger><SelectValue placeholder={t.select} /></SelectTrigger>
                    <SelectContent>{FINANCING_AMOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg space-y-4">
              <h4 className="font-medium text-[#1D314D]">{t.step2.ebitda}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t.step2.minEbitda}</Label>
                  <Select value={formData.minEbitda} onValueChange={v => updateField('minEbitda', v)}>
                    <SelectTrigger><SelectValue placeholder={t.select} /></SelectTrigger>
                    <SelectContent>{FINANCING_AMOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t.step2.maxEbitda}</Label>
                  <Select value={formData.maxEbitda} onValueChange={v => updateField('maxEbitda', v)}>
                    <SelectTrigger><SelectValue placeholder={t.select} /></SelectTrigger>
                    <SelectContent>{FINANCING_AMOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg space-y-4">
              <h4 className="font-medium text-[#1D314D]">{t.step2.cashFlow}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t.step2.minCashFlow}</Label>
                  <Select value={formData.minCashFlow} onValueChange={v => updateField('minCashFlow', v)}>
                    <SelectTrigger><SelectValue placeholder={t.select} /></SelectTrigger>
                    <SelectContent>{FINANCING_AMOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t.step2.maxCashFlow}</Label>
                  <Select value={formData.maxCashFlow} onValueChange={v => updateField('maxCashFlow', v)}>
                    <SelectTrigger><SelectValue placeholder={t.select} /></SelectTrigger>
                    <SelectContent>{FINANCING_AMOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <Label>{t.step2.dscrLabel} <span className="text-red-500">*</span></Label>
              <Input type="number" step="0.01" placeholder={t.step2.dscrPlaceholder} value={formData.debtServiceCoverageRatio} onChange={e => updateField('debtServiceCoverageRatio', e.target.value)} />
              <p className="text-xs text-gray-600 mt-1">{t.step2.dscrHelp}</p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#1D314D]">{t.step3.title}</h2>
            <div className="bg-[#1D314D]/10 border-l-4 border-[#CEAB65] p-4"><p className="text-sm text-[#1D314D]"><strong>Note:</strong> {t.step3.note}</p></div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#1D314D]">{t.step3.jurisdictionsTitle}</h3>
              
              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <Label className="font-semibold text-[#1D314D]">{t.step3.canadaRegion}</Label>
                <Select value="" onValueChange={v => {
                  if (v && !formData.jurisdiction_canada.includes(v)) {
                    updateField('jurisdiction_canada', [...formData.jurisdiction_canada, v]);
                  }
                }}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder={t.select} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {CANADIAN_PROVINCES[lang].map((province, idx) => (
                      <SelectItem key={idx} value={CANADIAN_PROVINCES.en[idx]}>{province}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.jurisdiction_canada.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.jurisdiction_canada.map((item, idx) => (
                      <span key={idx} className="bg-[#1D314D]/10 text-[#1D314D] px-2 py-1 rounded text-xs flex items-center gap-1">
                        {CANADIAN_PROVINCES[lang][CANADIAN_PROVINCES.en.indexOf(item)] || item}
                        <button type="button" onClick={() => updateField('jurisdiction_canada', formData.jurisdiction_canada.filter(i => i !== item))} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <Label className="font-semibold text-[#1D314D]">{t.step3.usaRegion}</Label>
                <Select value="" onValueChange={v => {
                  if (v && !formData.jurisdiction_usa.includes(v)) {
                    updateField('jurisdiction_usa', [...formData.jurisdiction_usa, v]);
                  }
                }}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder={t.select} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.jurisdiction_usa.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.jurisdiction_usa.map((item, idx) => (
                      <span key={idx} className="bg-[#1D314D]/10 text-[#1D314D] px-2 py-1 rounded text-xs flex items-center gap-1">
                        {item}
                        <button type="button" onClick={() => updateField('jurisdiction_usa', formData.jurisdiction_usa.filter(i => i !== item))} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <Label className="font-semibold text-[#1D314D]">{t.step3.europeRegion}</Label>
                <Select value="" onValueChange={v => {
                  if (v && !formData.jurisdiction_europe.includes(v)) {
                    updateField('jurisdiction_europe', [...formData.jurisdiction_europe, v]);
                  }
                }}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder={t.select} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {EUROPEAN_COUNTRIES[lang].map((country, idx) => (
                      <SelectItem key={idx} value={EUROPEAN_COUNTRIES.en[idx]}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.jurisdiction_europe.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.jurisdiction_europe.map((item, idx) => (
                      <span key={idx} className="bg-[#1D314D]/10 text-[#1D314D] px-2 py-1 rounded text-xs flex items-center gap-1">
                        {EUROPEAN_COUNTRIES[lang][EUROPEAN_COUNTRIES.en.indexOf(item)] || item}
                        <button type="button" onClick={() => updateField('jurisdiction_europe', formData.jurisdiction_europe.filter(i => i !== item))} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <Label className="font-semibold text-[#1D314D]">{t.step3.otherRegion}</Label>
                <Select value="" onValueChange={v => {
                  if (v && !formData.jurisdiction_others.includes(v)) {
                    updateField('jurisdiction_others', [...formData.jurisdiction_others, v]);
                  }
                }}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder={t.select} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {OTHER_COUNTRIES[lang].map((country, idx) => (
                      <SelectItem key={idx} value={OTHER_COUNTRIES.en[idx]}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.jurisdiction_others.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.jurisdiction_others.map((item, idx) => (
                      <span key={idx} className="bg-[#1D314D]/10 text-[#1D314D] px-2 py-1 rounded text-xs flex items-center gap-1">
                        {OTHER_COUNTRIES[lang][OTHER_COUNTRIES.en.indexOf(item)] || item}
                        <button type="button" onClick={() => updateField('jurisdiction_others', formData.jurisdiction_others.filter(i => i !== item))} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="border-t pt-6 space-y-6">
              <div className="space-y-4">
                <Label>{t.step3.creditScoreRequired} <span className="text-red-500">*</span></Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="creditScore" checked={formData.creditScoreRequired === 'yes'} onChange={() => updateField('creditScoreRequired', 'yes')} className="text-indigo-600" />{t.step3.yes}</label>
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="creditScore" checked={formData.creditScoreRequired === 'no'} onChange={() => updateField('creditScoreRequired', 'no')} className="text-indigo-600" />{t.step3.no}</label>
                </div>
                {errors.creditScoreRequired && <p className="text-red-500 text-xs">{errors.creditScoreRequired}</p>}
              </div>

              <div className="space-y-4">
                <Label>{t.step3.foreignCreditScoreAccepted} <span className="text-red-500">*</span></Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="foreignCredit" checked={formData.foreignCreditScoreAccepted === 'yes'} onChange={() => updateField('foreignCreditScoreAccepted', 'yes')} />{t.step3.yes}</label>
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="foreignCredit" checked={formData.foreignCreditScoreAccepted === 'no'} onChange={() => updateField('foreignCreditScoreAccepted', 'no')} />{t.step3.no}</label>
                </div>
              </div>

              {formData.foreignCreditScoreAccepted === 'yes' && (
                <div className="bg-[#1D314D]/10 p-4 rounded-lg space-y-4">
                  <Label className="text-[#1D314D] font-medium">{t.step3.acceptedCreditJurisdictions}</Label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-[#1D314D]">{t.step3.canadaRegion}</Label>
                      <Select value="" onValueChange={v => {
                        if (v && !formData.creditScoreCountries_canada.includes(v)) {
                          updateField('creditScoreCountries_canada', [...formData.creditScoreCountries_canada, v]);
                        }
                      }}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder={t.select} /></SelectTrigger>
                        <SelectContent className="bg-white max-h-48">
                          {CANADIAN_PROVINCES[lang].map((province, idx) => (
                            <SelectItem key={idx} value={CANADIAN_PROVINCES.en[idx]}>{province}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.creditScoreCountries_canada.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {formData.creditScoreCountries_canada.map((item, idx) => (
                            <span key={idx} className="bg-[#1D314D]/20 text-[#1D314D] px-2 py-0.5 rounded text-xs flex items-center gap-1">
                              {CANADIAN_PROVINCES[lang][CANADIAN_PROVINCES.en.indexOf(item)] || item}
                              <button type="button" onClick={() => updateField('creditScoreCountries_canada', formData.creditScoreCountries_canada.filter(i => i !== item))} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm text-[#1D314D]">{t.step3.usaRegion}</Label>
                      <Select value="" onValueChange={v => {
                        if (v && !formData.creditScoreCountries_usa.includes(v)) {
                          updateField('creditScoreCountries_usa', [...formData.creditScoreCountries_usa, v]);
                        }
                      }}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder={t.select} /></SelectTrigger>
                        <SelectContent className="bg-white max-h-48">
                          {US_STATES.map((state) => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.creditScoreCountries_usa.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {formData.creditScoreCountries_usa.map((item, idx) => (
                            <span key={idx} className="bg-[#1D314D]/20 text-[#1D314D] px-2 py-0.5 rounded text-xs flex items-center gap-1">
                              {item}
                              <button type="button" onClick={() => updateField('creditScoreCountries_usa', formData.creditScoreCountries_usa.filter(i => i !== item))} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm text-[#1D314D]">{t.step3.europeRegion}</Label>
                      <Select value="" onValueChange={v => {
                        if (v && !formData.creditScoreCountries_europe.includes(v)) {
                          updateField('creditScoreCountries_europe', [...formData.creditScoreCountries_europe, v]);
                        }
                      }}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder={t.select} /></SelectTrigger>
                        <SelectContent className="bg-white max-h-48">
                          {EUROPEAN_COUNTRIES[lang].map((country, idx) => (
                            <SelectItem key={idx} value={EUROPEAN_COUNTRIES.en[idx]}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.creditScoreCountries_europe.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {formData.creditScoreCountries_europe.map((item, idx) => (
                            <span key={idx} className="bg-[#1D314D]/20 text-[#1D314D] px-2 py-0.5 rounded text-xs flex items-center gap-1">
                              {EUROPEAN_COUNTRIES[lang][EUROPEAN_COUNTRIES.en.indexOf(item)] || item}
                              <button type="button" onClick={() => updateField('creditScoreCountries_europe', formData.creditScoreCountries_europe.filter(i => i !== item))} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm text-[#1D314D]">{t.step3.otherRegion}</Label>
                      <Select value="" onValueChange={v => {
                        if (v && !formData.creditScoreCountries_others.includes(v)) {
                          updateField('creditScoreCountries_others', [...formData.creditScoreCountries_others, v]);
                        }
                      }}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder={t.select} /></SelectTrigger>
                        <SelectContent className="bg-white max-h-48">
                          {OTHER_COUNTRIES[lang].map((country, idx) => (
                            <SelectItem key={idx} value={OTHER_COUNTRIES.en[idx]}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.creditScoreCountries_others.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {formData.creditScoreCountries_others.map((item, idx) => (
                            <span key={idx} className="bg-[#1D314D]/20 text-[#1D314D] px-2 py-0.5 rounded text-xs flex items-center gap-1">
                              {OTHER_COUNTRIES[lang][OTHER_COUNTRIES.en.indexOf(item)] || item}
                              <button type="button" onClick={() => updateField('creditScoreCountries_others', formData.creditScoreCountries_others.filter(i => i !== item))} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <Label>{t.step3.foreignCollateralAccepted} <span className="text-red-500">*</span></Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="foreignCollateral" checked={formData.foreignCollateralAccepted === 'yes'} onChange={() => updateField('foreignCollateralAccepted', 'yes')} />{t.step3.yes}</label>
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="foreignCollateral" checked={formData.foreignCollateralAccepted === 'no'} onChange={() => updateField('foreignCollateralAccepted', 'no')} />{t.step3.no}</label>
                </div>
              </div>

              {formData.foreignCollateralAccepted === 'yes' && (
                <div className="bg-[#CEAB65]/10 p-4 rounded-lg">
                  <Label className="text-[#1D314D] font-medium">{t.step3.foreignCollateralTypes}</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                    {COLLATERAL_OPTIONS.map(opt => (
                      <div key={opt.key} onClick={() => toggleArrayField('foreignCollateralOptions', opt.key)}
                        className={cn("cursor-pointer p-2 text-center rounded border text-sm transition-all",
                          formData.foreignCollateralOptions.includes(opt.key) ? "bg-[#CEAB65] text-[#1D314D] border-[#CEAB65]" : "bg-white text-slate-800 hover:bg-[#CEAB65]/10 border-slate-300"
                        )}>
                        {opt[lang]}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#1D314D]">{t.step4.title}</h2>
            <p className="text-gray-600">
              {lang === 'fr' 
                ? "Veuillez lire et accepter nos Termes et Conditions avant de continuer."
                : "Please read and accept our Terms and Conditions before proceeding."}
            </p>
            
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <label className="block text-sm font-medium text-[#1D314D] mb-3">
                {lang === 'fr' ? "Termes et Conditions" : "Terms and Conditions"} <span className="text-red-500">*</span>
              </label>
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-3 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1D314D] transition-colors font-medium"
              >
                {lang === 'fr' ? "Voir les Termes et Conditions complets" : "View Full Terms & Conditions"}
                <ExternalLink size={18} />
              </a>
              <p className="text-xs text-gray-500 mt-2">
                {lang === 'fr' 
                  ? "Inclut l'Annexe D spécifique aux Prêteurs"
                  : "Includes Annex D specific to Lenders"}
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-[#CEAB65]/10 border-l-4 border-[#CEAB65] rounded">
                <Checkbox checked={formData.agreeTerms} onCheckedChange={c => updateField('agreeTerms', c)} />
                <div><p className="font-medium text-[#1D314D]">{t.step4.agreeTerms} <span className="text-red-500">*</span></p><p className="text-xs text-slate-600">{t.step4.agreeTermsHelp}</p></div>
              </div>
              {errors.agreeTerms && <p className="text-red-500 text-xs ml-7">{errors.agreeTerms}</p>}
              
              <div className="flex items-start gap-3 p-4 bg-[#1D314D]/10 border-l-4 border-[#1D314D] rounded">
                <Checkbox checked={formData.backgroundCheckAuth} onCheckedChange={c => updateField('backgroundCheckAuth', c)} />
                <div><p className="font-medium text-[#1D314D]">{t.step4.backgroundCheck} <span className="text-red-500">*</span></p><p className="text-xs text-slate-600">{t.step4.backgroundCheckHelp}</p></div>
              </div>
              {errors.backgroundCheckAuth && <p className="text-red-500 text-xs ml-7">{errors.backgroundCheckAuth}</p>}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <DiditVerification
              role="lender"
              entityType={entityType}
              onApproved={handleVerificationApproved}
              onDeclined={handleVerificationDeclined}
            />
          </div>
        )}

          {step < 5 && (
            <div className="flex gap-3 pt-6 border-t mt-8">
              {step > 1 ? (
                <Button type="button" variant="outline" onClick={prevStep} className="flex-1 border-slate-300 hover:bg-slate-50">
                  <ArrowLeft className="mr-2 h-4 w-4" />{t.buttons.back}
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => navigate('/login')} className="flex-1 border-slate-300 hover:bg-slate-50">
                  <ArrowLeft className="mr-2 h-4 w-4" />{t.buttons.backToLogin}
                </Button>
              )}
              {step < 4 ? (
                <Button type="button" onClick={nextStep} className="flex-1 bg-[#1D314D] hover:bg-[#2E4663]">
                  {t.buttons.next}<ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button type="button" onClick={handleSubmit} disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t.buttons.submitting}</> : t.buttons.submit}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="text-center mt-6">
        <p className="text-slate-500 text-sm">
          {lang === 'en' ? 'Already a member?' : 'Déjà membre?'}{' '}
          <a href="/login" className="text-[#1D314D] hover:text-[#CEAB65] font-medium">
            {lang === 'en' ? 'Log in' : 'Se connecter'}
          </a>
        </p>
      </div>
    </div>
  );
}